import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { cx } from "./basics";

/**
 * Text clamped to five lines (UX-211). A "Show more" / "Show less" button appears only when the
 * text really overflows, measured after render and whenever the text's box changes size (window
 * resize, a late font load, starting hidden). The button stops propagation so a clickable row
 * around it does not also open.
 */
export function ClampedText({ text, className }: { text: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const id = useId();
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);

  const measure = useCallback(() => {
    const el = ref.current;
    // While expanded there is nothing to measure; the toggle stays so it can collapse again.
    if (el && !open) setOverflows(el.scrollHeight > el.clientHeight);
  }, [open]);

  useLayoutEffect(measure, [measure, text]);
  useEffect(() => {
    const el = ref.current;
    if (typeof ResizeObserver === "undefined" || !el) {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    return () => ro.disconnect();
  }, [measure]);

  return (
    <>
      <span id={id} ref={ref} className={cx("sub", "task-desc", !open && "clamp-5", className)}>
        {text}
      </span>
      {(overflows || open) && (
        <button
          type="button"
          className="link-btn clamp-toggle"
          aria-expanded={open}
          aria-controls={id}
          onClick={(e) => {
            e.stopPropagation();
            setOpen(!open);
          }}
        >
          {open ? "Show less" : "Show more"}
        </button>
      )}
    </>
  );
}
